/**
 * M449.1 — IDENTIDADE DE CAMINHO num produto que roda em DOIS sistemas ao mesmo tempo.
 *
 * O Lenovo (Windows) é o hub e guarda o estado; o VPS (Linux) é destino produtivo de sessão desde a
 * `0.450.0`. Até aqui existia uma única normalização, `normalizeSessionCwd`, que fazia `.toLowerCase()`
 * "porque Windows é case-insensitive". Em Linux isso está ERRADO e não é sutil: `/proj/App` e `/proj/app`
 * são pastas DIFERENTES, e colapsá-las funde dois projetos distintos.
 *
 * ## A regra: a plataforma que importa é a do CAMINHO, não a do PROCESSO
 *
 * `process.platform` é a resposta errada, e a razão é concreta: o servidor roda em Windows e compara cwd de
 * sessão que vive no Linux. Quem decide a semântica é a FORMA do caminho:
 *
 * - `C:/…`, `\\servidor\share\…` ou qualquer coisa com letra de unidade → semântica **Windows**:
 *   separador unificado, sem barra final, **case-insensitive**;
 * - `/home/…`, `/tmp/…` → semântica **POSIX**: sem barra final, **case-SENSITIVE**, e a contrabarra é
 *   caractere VÁLIDO DE NOME — converter `\` em `/` ali corromperia o caminho.
 *
 * ## Por que NÃO se preservou a caixa em todo lugar (a alternativa que foi descartada)
 *
 * Seria mais simples de raciocinar, e foi medido antes de descartar: há **89 registros já gravados
 * achatados** no estado de produção (56 em JSON — `communication-validation-policy` 45,
 * `project-cwd-identities` 7, `evidence-judge-availability` 3, `client-inbound` 1 — e 33 no SQLite, em
 * `ProjectFlag.cwd`, `PendingQuestion.subjectCwd`, `MasterEvent.subjectCwd`, `ProjectCwdBinding.cwd` e
 * outras seis tabelas). **Todos os 89 são caminhos Windows** (`c:/…`), zero POSIX.
 *
 * Preservar caixa em todo lugar tornaria esses 89 inalcançáveis de um instante para o outro — perder-se-iam
 * autorização de projeto, pergunta pendente de cliente e continuidade —, e exigiria migração de dados com
 * backup, janela e reconciliação. Manter a semântica Windows PARA CAMINHO WINDOWS mantém os 89 casando e
 * corrige o Linux, que é o que estava quebrado. A migração deixa de ser necessária **por decisão, não por
 * esquecimento**, e esta é a explicação de por quê.
 *
 * ## O que esta função NÃO é
 *
 * Não é identidade FÍSICA de diretório. Essa vive em `project-cwd-orphan.ts` (volumeId + fileId) e pertence
 * ao M433/M226; um caminho renomeado continua sendo outro caminho aqui. Esta função responde "é a mesma
 * string de pasta?", não "é o mesmo diretório no disco?".
 */
/**
 * Deduz a semântica pela forma. Fail-safe para `posix`: é a mais RESTRITIVA das duas (distingue mais
 * caminhos), então errar para cá nunca funde duas pastas diferentes — no máximo deixa de unir duas grafias
 * da mesma. O erro oposto é o que apaga dado.
 */
export function pathFlavorOf(p) {
    const t = p.trim();
    if (/^[a-zA-Z]:[\\/]/.test(t))
        return 'windows'; // C:/… ou C:\…
    if (t.startsWith('\\\\'))
        return 'windows'; // \\servidor\share
    return 'posix';
}
/**
 * Chave de comparação de um caminho. Duas strings com a MESMA chave designam a mesma pasta **sob a
 * semântica daquele sistema**.
 *
 * O `flavor` pode ser forçado por quem já sabe a origem (por exemplo, um cwd que veio de um device remoto
 * declarado como Linux); omitido, é deduzido da forma.
 *
 * Omitido o `flavor`, a chave fica guardada pela própria string (#306, 23/10: `pathKey` + `pathFlavorOf`
 * eram 14% do tempo próprio do laço travado das 09:48, chamados por sessão × condução nos tiques). A função
 * é pura e os cwds são poucos; o teto só impede crescimento sem limite com caminhos de uso único.
 */
export function pathKey(p, flavor) {
    if (flavor !== undefined)
        return computePathKey(p, flavor);
    const cached = PATH_KEY_CACHE.get(p);
    if (cached !== undefined)
        return cached;
    const key = computePathKey(p, pathFlavorOf(p));
    if (PATH_KEY_CACHE.size >= PATH_KEY_CACHE_MAX)
        PATH_KEY_CACHE.clear();
    PATH_KEY_CACHE.set(p, key);
    return key;
}
const PATH_KEY_CACHE_MAX = 4096;
const PATH_KEY_CACHE = new Map();
/** Só para teste: quantas chaves estão guardadas. */
export function pathKeyCacheSize() {
    return PATH_KEY_CACHE.size;
}
function computePathKey(p, flavor) {
    if (flavor === 'windows') {
        return p
            .replace(/[\\/]+/g, '/')
            .replace(/\/+$/, '')
            .toLowerCase();
    }
    /*
     * POSIX: colapsa só a BARRA repetida (`//` é equivalente a `/` no POSIX, salvo o caso de duas barras
     * iniciais, que a spec deixa a critério da implementação e que o Linux trata como uma). A contrabarra
     * NÃO é tocada — em Linux `a\b` é um arquivo chamado `a\b`, e "normalizá-lo" inventaria outro caminho.
     */
    return p.replace(/\/{2,}/g, '/').replace(/\/+$/, '');
}
/** Duas grafias designam a mesma pasta? */
export function samePath(a, b) {
    const fa = pathFlavorOf(a);
    const fb = pathFlavorOf(b);
    // Formas de sistemas diferentes nunca são a mesma pasta — e comparar sob uma das semânticas seria
    // escolher arbitrariamente qual das duas mente.
    if (fa !== fb)
        return false;
    return pathKey(a, fa) === pathKey(b, fb);
}
//# sourceMappingURL=path-identity.js.map